import { StandardEvents } from '@shopify/events';

/*
 * The chosen plan row is the whole selection: it carries both the variant it
 * buys and the price to show, and this keeps the form on both.
 *
 * The variant is the reason there is no separate picker. Each row is a variant
 * plus the plan allocated to it — a filter's pack, interval and plan are one
 * axis — so the row's data-variant-id is written into the form's hidden
 * name="id", and the add posts the variant the shopper actually chose. Without
 * this the hidden id keeps the server's first-available variant while a later
 * row's plan rides on top of it, which is a plan on a variant it is not
 * allocated to.
 *
 * The price is never computed here. snippets/pp-subscription-plan.liquid already
 * works each row's total out from that plan's own price adjustment and writes it
 * onto the radio, so this only copies it across. Two prices computed two ways is
 * how they end up disagreeing. Not every form carries a price node — the cart
 * drawer draws no button price — so the price step is skipped where there is
 * none, but the variant step still runs.
 *
 * Scoped by form rather than by page. The cart drawer renders rows with the same
 * name under every page, so a document wide query would let a click in the drawer
 * rewrite the product page's form; a row resolves to its own form and touches
 * nothing else.
 */
const PRICE = '.pp-add-to-cart__price';
const PRICE_BOX = '.pp-product-price';
const ROW = 'input[name="selling_plan"]';
const VARIANT_ID = 'input[name="id"]';

const REDUCED_MOTION = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * The little landing a changed figure makes: a breath of fade and a settle
 * upward. Run per write rather than kept as a CSS class because a class
 * would need taking off again to run twice, and the next change can come
 * from any row at any time.
 *
 * @param {HTMLElement} node
 */
const settle = (node) => {
  if (REDUCED_MOTION.matches) return;
  node.animate(
    [
      { opacity: 0.2, transform: 'translateY(3px)' },
      { opacity: 1, transform: 'none' },
    ],
    { duration: 180, easing: 'ease-out' }
  );
};

/**
 * Writes the row's text into a node, remembering the node's own first text as
 * the base to fall back to. That base is the server's answer for the plain
 * variant — a real compare-at sale, say — so a row without a discount hands
 * the node back what it said before the rows ever touched it. The base is
 * kept on the node itself because a re-render replaces the node and its
 * memory together, which is exactly when the memory must reset.
 *
 * Only an actual change lands with the settle animation; writing a node the
 * same text is left invisible, or every re-sync would twitch every figure.
 *
 * @param {Element | null} node
 * @param {string | undefined} text
 */
const write = (node, text) => {
  if (!(node instanceof HTMLElement)) return;
  if (node.dataset.base === undefined) node.dataset.base = node.textContent.trim();

  const next = text ?? node.dataset.base;
  if (node.textContent.trim() === next.trim()) return;

  node.textContent = next;
  settle(node);
};

/** @param {HTMLFormElement | null} form */
const sync = (form) => {
  if (!form) return;

  const chosen = form.querySelector(`${ROW}:checked`);
  if (!(chosen instanceof HTMLInputElement)) return;

  const variantId = chosen.dataset.variantId;
  if (variantId) {
    const idField = form.querySelector(VARIANT_ID);
    if (idField instanceof HTMLInputElement) idField.value = variantId;
  }

  const price = form.querySelector(PRICE);
  if (price instanceof HTMLElement) {
    const total = chosen.dataset.total;
    if (total && price.textContent !== total) {
      price.textContent = total;
      settle(price);
    }
  }

  // The price block at the top of the buy box follows the chosen row too:
  // the discounted total as the price, the undiscounted figure struck
  // through, and the savings badge naming the difference. The row's
  // data-compare and data-saving only exist on a discounted row, so an
  // Einmalkauf row passes undefined and the nodes fall back to their base.
  // Empty nodes are hidden by an :empty rule in pp-blocks.css, not removed.
  const box = form.querySelector(PRICE_BOX);
  if (box) {
    const badge = box.querySelector('.pp-product-price__savings');
    const badgeText =
      chosen.dataset.saving && badge instanceof HTMLElement
        ? [badge.dataset.label, chosen.dataset.saving].filter(Boolean).join(' ')
        : undefined;

    write(box.querySelector('.pp-product-price__current'), chosen.dataset.total);
    write(box.querySelector('.pp-product-price__compare'), chosen.dataset.compare);
    write(badge, badgeText);
  }
};

/*
 * Every form that currently has a row chosen. Idempotent and cheap, which is
 * what lets it be the answer after a re-render as well: nothing has to hold a
 * reference to an element the morph may have replaced.
 */
const syncAll = () => {
  for (const row of document.querySelectorAll(`${ROW}:checked`)) {
    if (row instanceof HTMLInputElement) sync(row.form);
  }
};

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.name !== 'selling_plan') return;

  sync(target.form);
});

/*
 * A variant change re-renders the section from the server, which brings the
 * button back printing the plain variant price while the row the server marks
 * chosen may carry an adjustment. The two disagree until this runs again.
 *
 * The event carries a promise for the answer, not for the morph that applies
 * it, so the live DOM is read a frame later rather than out of the answer.
 */
document.addEventListener(StandardEvents.productSelect, (event) => {
  const target = event.target;

  // A swatch on a product card announces the same event and means nothing here.
  if (!(target instanceof Element) || target.closest('product-card')) return;

  event.promise
    .then(() => {
      requestAnimationFrame(syncAll);
    })
    .catch(() => {
      // An aborted or failed variant request leaves the price where it is.
    });
});

/*
 * The first paint is the one case the server cannot get right on its own: the
 * button is a block of its own and cannot see which row the picker beside it
 * marks chosen, and giving it a second copy of that setting would be two
 * sources for one answer. So the agreed price is applied here, as early as a
 * module runs.
 */
syncAll();
